import React from 'react';
import { Card, CardContent } from './card';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { TrendingUp, Users, DollarSign, Calendar, Anchor, PieChart as PieIcon, ShoppingCart, Fuel, MapPin, Truck } from 'lucide-react';

const NAVY = '#0A2540';
const CORAL = '#C4724F';
const GOLD = '#D4AF37';
const GREEN = '#10B981';
const BLUE = '#3B82F6';
const RED = '#EF4444';
const ORANGE = '#F59E0B';
const PURPLE = '#8B5CF6';

function formatEur(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}

function StatCard({ label, value, sub, color, icon: Icon, small }) {
  return (
    <Card>
      <CardContent className={small ? "p-3" : "p-4"}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
            <p className={`${small ? 'text-lg' : 'text-2xl'} font-bold mt-1`} style={{ color }}>{value}</p>
            {sub && <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{sub}</p>}
          </div>
          {Icon && <Icon className="h-4 w-4 opacity-20 flex-shrink-0" style={{ color }} />}
        </div>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg rounded-lg p-3 border text-xs max-w-[220px]">
      <p className="font-bold text-gray-700 mb-1">Semaine {label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex justify-between gap-3">
          <span className="truncate">{p.name}</span>
          <span className="font-mono font-bold whitespace-nowrap">
            {p.name.includes('assager') ? p.value : formatEur(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
};

export function FinancialDashboard({ data, viewMode = 'admin' }) {
  if (!data?.enabled) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <TrendingUp className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Commission variable non activée sur ce contrat</p>
        </CardContent>
      </Card>
    );
  }

  const { fixed_amount, commission_rate, passenger_threshold, avg_price_per_passenger,
    total_weeks, realized, projected, weekly } = data;

  const isOwner = viewMode === 'owner';

  // Pie: Répartition CA potentiel
  const pieData = isOwner ? [
    { name: 'Rétrocession propriétaire', value: projected.owner_total, color: GOLD },
    { name: 'Marge nette SOGNUDIMARE', value: Math.max(0, projected.margin_nette || 0), color: NAVY },
  ] : [
    { name: 'Rétrocession propriétaire', value: projected.owner_total, color: GOLD },
    { name: 'Charges d\'exploitation', value: projected.charges?.total || 0, color: ORANGE },
    { name: 'Marge nette SOGNUDIMARE', value: Math.max(0, projected.margin_nette || 0), color: NAVY },
  ];

  // Bar: Réalisé vs Potentiel
  const barData = isOwner ? [
    { name: 'Réalisé', ca: realized.revenue, revenus_proprio: realized.owner_total, marge_nette: realized.margin_nette || 0 },
    { name: `Potentiel (${total_weeks} sem.)`, ca: projected.total_revenue, revenus_proprio: projected.owner_total, marge_nette: projected.margin_nette || 0 },
  ] : [
    { name: 'Réalisé', ca: realized.revenue, retrocession: realized.owner_total, charges: realized.charges?.total || 0, marge_nette: realized.margin_nette || 0 },
    { name: `Potentiel (${total_weeks} sem.)`, ca: projected.total_revenue, retrocession: projected.owner_total, charges: projected.charges?.total || 0, marge_nette: projected.margin_nette || 0 },
  ];

  return (
    <div className="space-y-4" data-testid="financial-dashboard">
      {/* Title */}
      <div className="flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-[#C4724F]" />
        <h3 className="text-base font-bold text-[#0A2540]">
          {isOwner ? 'Bilan financier de votre navire' : 'Projection financière & Rétrocession'}
        </h3>
      </div>

      {/* Config summary */}
      <div className="bg-gray-50 rounded-xl p-3 sm:p-4 text-xs text-gray-600 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 border">
        <div>Seuil passagers : <strong className="text-[#0A2540]">{passenger_threshold} pax</strong></div>
        <div>Commission : <strong className="text-[#C4724F]">{commission_rate}%</strong> au-delà</div>
        <div>Prix moyen : <strong className="text-[#0A2540]">{formatEur(avg_price_per_passenger)}/pax</strong></div>
        <div>Saison : <strong className="text-[#0A2540]">{total_weeks} semaines</strong></div>
      </div>

      {/* KPI Row 1: CA */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Chiffre d'affaires réalisé" value={formatEur(realized.revenue)}
          sub={`${realized.passengers} passagers / ${data.realized_weeks} sem.`} color={NAVY} icon={DollarSign} />
        <StatCard label={`CA potentiel (${total_weeks} semaines)`} value={formatEur(projected.total_revenue)}
          sub={`${projected.total_passengers} passagers`} color={BLUE} icon={TrendingUp} />
        <StatCard label={isOwner ? "Vos revenus actuels" : "Revenus propriétaire actuels"}
          value={formatEur(realized.owner_total)}
          sub={`Fixe ${formatEur(fixed_amount)} + Commission ${formatEur(realized.commission)}`} color={GOLD} icon={Anchor} />
      </div>

      {/* KPI Row 2: Revenus potentiel + Charges + Marges */}
      <div className={`grid gap-3 ${isOwner ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'}`}>
        <StatCard label={isOwner ? `Vos revenus potentiels (${total_weeks} sem.)` : `Rétrocession potentielle (${total_weeks} sem.)`}
          value={formatEur(projected.owner_total)}
          sub={`Fixe ${formatEur(fixed_amount)} + Commission ${formatEur(projected.commission)}`} color={GOLD} icon={Anchor} small />
        {!isOwner && (
          <StatCard label="Charges d'exploitation potentielles"
            value={formatEur(projected.charges?.total || 0)}
            sub={`Nourriture, essence, ports, transports`} color={ORANGE} icon={ShoppingCart} small />
        )}
        <StatCard label={`Marge nette potentielle (${total_weeks} sem.)`}
          value={formatEur(projected.margin_nette || 0)}
          sub={`CA - Rétrocession - Charges`} color={GREEN} icon={PieIcon} small />
        <StatCard label="Marge nette actuelle"
          value={formatEur(realized.margin_nette || 0)}
          sub={`Sur la base du CA réalisé`} color={realized.margin_nette >= 0 ? GREEN : RED} icon={DollarSign} small />
      </div>

      {/* Charges detail - admin only */}
      {!isOwner && data.charges_config && (
        <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
          <p className="text-xs font-bold text-orange-800 mb-2">Détail des charges d'exploitation</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-orange-700">
            <div className="flex items-center gap-1.5"><ShoppingCart className="h-3 w-3" /> Nourriture & boissons : <strong>{data.charges_config.food_per_pax_per_week}€/pax/sem.</strong></div>
            <div className="flex items-center gap-1.5"><Fuel className="h-3 w-3" /> Essence : <strong>{data.charges_config.fuel_per_week}€/sem.</strong></div>
            <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Ports : <strong>{data.charges_config.ports_per_week}€/sem.</strong></div>
            <div className="flex items-center gap-1.5"><Truck className="h-3 w-3" /> Transport A/R : <strong>{data.charges_config.transport_per_pax}€/pax</strong></div>
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* CA + Marges evolution */}
        <Card>
          <CardContent className="p-4">
            <h4 className="text-sm font-bold text-[#0A2540] mb-3">CA cumulé & Marges par semaine</h4>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} label={{ value: 'Semaine', position: 'insideBottom', offset: -2, fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v/1000)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cumul_revenue" name="CA cumulé" stroke={NAVY} fill={NAVY} fillOpacity={0.08} strokeWidth={2.5} />
                <Area type="monotone" dataKey="owner_total" name={isOwner ? "Vos revenus" : "Rétrocession propriétaire"} stroke={GOLD} fill={GOLD} fillOpacity={0.12} strokeWidth={2} />
                <Area type="monotone" dataKey="margin_nette" name="Marge nette SOGNUDIMARE" stroke={GREEN} fill={GREEN} fillOpacity={0.1} strokeWidth={2} />
                {!isOwner && <Area type="monotone" dataKey="cumul_charges" name="Charges cumulées" stroke={ORANGE} fill={ORANGE} fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 2" />}
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-1 text-[10px]">
              {weekly.some(w => w.status === 'realized') && (
                <span className="text-gray-400">Zone pleine = réalisé | Zone claire = prévisionnel</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie */}
        <Card>
          <CardContent className="p-4">
            <h4 className="text-sm font-bold text-[#0A2540] mb-3">Répartition du CA potentiel ({formatEur(projected.total_revenue)})</h4>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                  paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ strokeWidth: 1 }} fontSize={10}>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatEur(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Passengers & Commission */}
      <Card>
        <CardContent className="p-4">
          <h4 className="text-sm font-bold text-[#0A2540] mb-3">Passagers cumulés & Commission variable</h4>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="pax" tick={{ fontSize: 10 }} orientation="left" />
              <YAxis yAxisId="eur" tick={{ fontSize: 10 }} orientation="right" tickFormatter={v => `${Math.round(v/1000)}k€`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="pax" type="monotone" dataKey="cumul_passengers" name="Passagers cumulés" stroke={BLUE} strokeWidth={2} dot={false} />
              <Line yAxisId="eur" type="monotone" dataKey="cumul_commission" name="Commission cumulée" stroke={CORAL} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
            Commission de <strong>{commission_rate}%</strong> applicable au-delà de <strong>{passenger_threshold} passagers</strong> (seuil acquis).
            Chaque passager supplémentaire à <strong>{formatEur(avg_price_per_passenger)}</strong> génère <strong>{formatEur(avg_price_per_passenger * commission_rate / 100)}</strong> de commission.
          </div>
        </CardContent>
      </Card>

      {/* Bar: Réalisé vs Potentiel */}
      <Card>
        <CardContent className="p-4">
          <h4 className="text-sm font-bold text-[#0A2540] mb-3">Compte de résultat : Réalisé vs Potentiel ({total_weeks} semaines)</h4>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData} barGap={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v/1000)}k€`} />
              <Tooltip formatter={(v) => formatEur(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="ca" name="Chiffre d'affaires" fill={NAVY} radius={[4,4,0,0]} />
              {isOwner ? (<>
                <Bar dataKey="revenus_proprio" name="Vos revenus" fill={GOLD} radius={[4,4,0,0]} />
                <Bar dataKey="marge_nette" name="Marge nette SOGNUDIMARE" fill={GREEN} radius={[4,4,0,0]} />
              </>) : (<>
                <Bar dataKey="retrocession" name="Rétrocession propriétaire" fill={GOLD} radius={[4,4,0,0]} />
                <Bar dataKey="charges" name="Charges d'exploitation" fill={ORANGE} radius={[4,4,0,0]} />
                <Bar dataKey="marge_nette" name="Marge nette" fill={GREEN} radius={[4,4,0,0]} />
              </>)}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
